import { action, computed, observable } from "mobx";
import { isEmptyOrWhitespace, ModelBase } from "@shoothill/core";
import { validateFourDecimalPlaces } from "Utils/Utils";

export class OrderEditLineModel extends ModelBase<OrderEditLineModel> {
    // #region Constructors and Disposers
    // #endregion Constructors and Disposers

    // #region Constants and Defaults

    public static readonly DEFAULT_MATERIALID = null;
    public static readonly DEFAULT_CATEGORYID = null;
    public static readonly DEFAULT_CATEGORYDISPLAYNAME = "";
    public static readonly DEFAULT_SUBCATEGORYID = null;
    public static readonly DEFAULT_SUBCATEGORYDISPLAYNAME = "";
    public static readonly DEFAULT_LINEDESCRIPTIONID = null;
    public static readonly DEFAULT_VARIATIONID = null;
    public static readonly DEFAULT_LINEDESCRIPTIONDISPLAYNAME = "";

    public static readonly DEFAULT_ITEMDESCRIPTION = "";
    public static readonly DEFAULT_UNITS = 0;
    public static readonly DEFAULT_UNITPRICE = 0;

    public static readonly DEFAULT_DATEFROM = null;
    public static readonly DEFAULT_DATETO = null;
    public static readonly DEFAULT_ISNA = false;

    // #endregion Constants and Defaults

    // #region Properties

    @observable
    public materialId: string | null = OrderEditLineModel.DEFAULT_MATERIALID;

    @observable
    public categoryId: string | null = OrderEditLineModel.DEFAULT_CATEGORYID;

    @observable
    public categoryDisplayName: string = OrderEditLineModel.DEFAULT_CATEGORYDISPLAYNAME;

    @observable
    public subCategoryId: string | null = OrderEditLineModel.DEFAULT_SUBCATEGORYID;

    @observable
    public subCategoryDisplayName: string = OrderEditLineModel.DEFAULT_SUBCATEGORYDISPLAYNAME;

    @observable
    public lineDescriptionId: string | null = OrderEditLineModel.DEFAULT_LINEDESCRIPTIONID;

    @observable
    public variationId: string | null = OrderEditLineModel.DEFAULT_VARIATIONID;

    @observable
    public lineDescriptionDisplayName: string = OrderEditLineModel.DEFAULT_LINEDESCRIPTIONDISPLAYNAME;

    @observable
    public itemDescription: string = OrderEditLineModel.DEFAULT_ITEMDESCRIPTION;

    @observable
    public units: number = OrderEditLineModel.DEFAULT_UNITS;

    @observable
    public unitPrice: number = OrderEditLineModel.DEFAULT_UNITPRICE;

    @observable
    public dateFrom: string | null = OrderEditLineModel.DEFAULT_DATEFROM;

    @observable
    public dateTo: string | null = OrderEditLineModel.DEFAULT_DATETO;

    @observable
    public isNA: boolean = OrderEditLineModel.DEFAULT_ISNA;

    // #endregion Properties

    // #region Actions

    @action
    public reset(fullReset: boolean = true): void {
        if (fullReset) {
            this.categoryId = OrderEditLineModel.DEFAULT_CATEGORYID;
            this.subCategoryId = OrderEditLineModel.DEFAULT_SUBCATEGORYID;
            this.lineDescriptionId = OrderEditLineModel.DEFAULT_LINEDESCRIPTIONID;
        }

        this.variationId = OrderEditLineModel.DEFAULT_VARIATIONID;
        this.materialId = OrderEditLineModel.DEFAULT_MATERIALID;
        this.itemDescription = OrderEditLineModel.DEFAULT_ITEMDESCRIPTION;
        this.units = OrderEditLineModel.DEFAULT_UNITS;
        this.unitPrice = OrderEditLineModel.DEFAULT_UNITPRICE;
        this.dateFrom = OrderEditLineModel.DEFAULT_DATEFROM; // Order type === Hire
        this.dateTo = OrderEditLineModel.DEFAULT_DATETO; // Order rtpe === Hire
        this.isNA = OrderEditLineModel.DEFAULT_ISNA; // Order rtpe === Hire
    }

    @action
    fromDto(dto: IOrderLineDto): void {
        this.materialId = dto.materialId ?? OrderEditLineModel.DEFAULT_MATERIALID;
        this.categoryId = dto.categoryId ?? OrderEditLineModel.DEFAULT_CATEGORYID;
        this.categoryDisplayName = dto.categoryDisplayName ?? OrderEditLineModel.DEFAULT_CATEGORYDISPLAYNAME;
        this.subCategoryId = dto.subCategoryId ?? OrderEditLineModel.DEFAULT_SUBCATEGORYID;
        this.subCategoryDisplayName = dto.subCategoryDisplayName ?? OrderEditLineModel.DEFAULT_SUBCATEGORYDISPLAYNAME;
        this.lineDescriptionId = dto.lineDescriptionId ?? OrderEditLineModel.DEFAULT_LINEDESCRIPTIONID;
        this.variationId = dto.variationId ?? OrderEditLineModel.DEFAULT_VARIATIONID;
        this.lineDescriptionDisplayName = dto.lineDescriptionDisplayName ?? OrderEditLineModel.DEFAULT_LINEDESCRIPTIONDISPLAYNAME;
        this.itemDescription = dto.itemDescription ?? OrderEditLineModel.DEFAULT_ITEMDESCRIPTION;
        this.units = dto.units ?? OrderEditLineModel.DEFAULT_UNITS;
        this.unitPrice = dto.unitPrice ?? OrderEditLineModel.DEFAULT_UNITPRICE;
        this.dateFrom = dto.dateFrom ?? OrderEditLineModel.DEFAULT_DATEFROM;
        this.dateTo = dto.dateTo ?? OrderEditLineModel.DEFAULT_DATETO;
        this.isNA = dto.isNA ?? OrderEditLineModel.DEFAULT_ISNA;
    }

    toDto(): IOrderLineDto {
        return {
            materialId: this.materialId,
            categoryId: this.categoryId,
            categoryDisplayName: this.categoryDisplayName,
            subCategoryId: this.subCategoryId,
            subCategoryDisplayName: this.subCategoryDisplayName,
            lineDescriptionId: this.lineDescriptionId,
            variationId: this.variationId,
            lineDescriptionDisplayName: this.lineDescriptionDisplayName,
            itemDescription: this.itemDescription,
            units: this.units,
            unitPrice: this.unitPrice,
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
            isNA: this.isNA,
        };
    }

    // #endregion Actions

    // #region Custom Validation

    @computed
    public get validateCategoryId(): string {
        // RULES
        // A category must be defined.
        return this.categoryId === OrderEditLineModel.DEFAULT_CATEGORYID ? "Please select a category" : "";
    }

    @computed
    public get validateSubCategoryId(): string {
        // RULES
        // An sub-category must be defined.
        return this.subCategoryId === OrderEditLineModel.DEFAULT_SUBCATEGORYID ? "Please select a sub-category" : "";
    }

    @computed
    public get validateLineDescriptionId(): string {
        // RULES
        // A line description must be defined.
        return this.lineDescriptionId === OrderEditLineModel.DEFAULT_LINEDESCRIPTIONID ? "Please select a line description" : "";
    }

    @computed
    public get validateItemDescription(): string {
        // RULES
        // The description must not be null or whitespace.
        return isEmptyOrWhitespace(this.itemDescription) ? "Please provide a description" : "";
    }

    @computed
    public get validateUnits(): string {
        // RULES
        // The number of units must be greater than zero.
        return this.units === OrderEditLineModel.DEFAULT_UNITS || isNaN(this.units) ? "Please set the number of units" : "";
    }

    @computed
    public get validateUnitPrice(): string {
        // RULES
        if (this.unitPrice === OrderEditLineModel.DEFAULT_UNITPRICE || isNaN(this.unitPrice)) {
            return "Please set the unit price";
        } else if (!validateFourDecimalPlaces(this.unitPrice)) {
            return "No more than four decimal places";
        }

        return "";
    }

    public validateMaterialId(isStockOrderLine: boolean): string {
        // RULES
        switch (true) {
            // If this is a stock orderline, the material must be defined.
            case isStockOrderLine && isEmptyOrWhitespace(this.materialId):
                return "Please select an item";

            // If this is not a stock orderline, the material must be not defined.
            case !isStockOrderLine && !isEmptyOrWhitespace(this.materialId):
                return "An item cannot be selected for a non-stock orderline";

            default:
                return "";
        }
    }

    // #endregion Custom Validation
}

export interface IOrderLineDto {
    materialId: string | null;
    categoryId: string | null;
    categoryDisplayName: string;
    subCategoryId: string | null;
    subCategoryDisplayName: string;
    lineDescriptionId: string | null;
    variationId: string | null;
    lineDescriptionDisplayName: string;
    itemDescription: string;
    units: number;
    unitPrice: number;
    dateFrom: string | null;
    dateTo: string | null;
    isNA: boolean;
}
